import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { usePrevious } from 'lib/usePrevious';
import { User } from 'lib/model';
import TransitionDialog from 'components/transition-dialog';
import { CloudUploadIcon as CloudUploadOutlineIcon } from '@heroicons/react/outline';
import {
  CloudUploadIcon as CloudUploadSolidIcon,
  XCircleIcon,
} from '@heroicons/react/solid';

export default function FileUploadDialog({
  open,
  setOpen,
  user,
  onUpload,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: User;
  onUpload: (selectedFiles: File[]) => void;
}): ReactElement {
  const previousOpen = usePrevious(open);
  const cancelButtonRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>();
  const [uploadClicked, setUploadClicked] = useState(false);

  useEffect(() => {
    if (!previousOpen && open) {
      setSelectedFiles(undefined);
      setUploadClicked(false);
    }
  }, [open, previousOpen]);

  function appendFiles(fileList: FileList) {
    const existingFiles = selectedFiles || [];
    const existingFileNames = existingFiles.map((f) => f.name);

    setSelectedFiles([
      ...existingFiles,
      ...[...fileList].filter((f) => !existingFileNames.includes(f.name)),
    ]);
  }

  function fileSizeMB(file: File): number {
    return Math.round((file.size / 1024 / 1024) * 10) / 10;
  }

  function errorForFile(file: File): string | null {
    return !file.type.startsWith('audio/') && !file.type.startsWith('video/')
      ? 'File must be Audio or Video'
      : file.size === 0
      ? 'File is empty'
      : file.size > 3500 * 1024 * 1024
      ? 'Maximum file size is 3 500 MB'
      : null;
  }

  return (
    <TransitionDialog open={open} initialFocus={cancelButtonRef}>
      <div
        className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4"
        onDragEnter={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onDragOver={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.stopPropagation();
          e.preventDefault();
          appendFiles(e.dataTransfer.files);
        }}
      >
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
            <CloudUploadSolidIcon
              className="h-6 w-6 text-blue-600"
              aria-hidden="true"
            />
          </div>
          <div className="mt-3 text-center min-w-0 sm:mt-0 sm:ml-4 sm:text-left">
            <Dialog.Title
              as="h3"
              className="text-lg leading-6 font-medium text-gray-900"
            >
              Upload files
            </Dialog.Title>
            <div className="mt-2">
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <CloudUploadOutlineIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Click here to select files</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        accept="audio/*,video/*"
                        multiple
                        onChange={(event) => {
                          appendFiles(event.target.files);
                          event.target.value = null;
                        }}
                      />
                    </label>
                    <p className="pl-1">or drag and drop them</p>
                  </div>
                  <p className="text-sm text-gray-900">
                    Audio or Video files only
                  </p>
                  <p className="text-sm text-gray-900">
                    Files must be less than <b>2h 30m</b> long
                  </p>
                </div>
              </div>

              {selectedFiles?.length > 0 && (
                <div className="mt-2">
                  <p className="font-bold">Selected files</p>
                  {selectedFiles.map((f) => (
                    <div
                      key={f.name}
                      className="w-full break-all inline-flex items-center"
                    >
                      <div>
                        <p>
                          {f.name} ({fileSizeMB(f)} MB)
                        </p>
                        {errorForFile(f) && (
                          <p className="text-red-500">{errorForFile(f)}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="ml-1 font-bold text-red-500 hover:text-red-700"
                        onClick={() =>
                          setSelectedFiles(
                            selectedFiles.filter((f2) => f2 !== f)
                          )
                        }
                      >
                        <XCircleIcon
                          className="mr-2 h-5 w-5"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-40 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={() => {
            setUploadClicked(true);
            onUpload(selectedFiles);
          }}
          disabled={
            !user ||
            !selectedFiles ||
            selectedFiles.length == 0 ||
            selectedFiles.some((f) => !!errorForFile(f)) ||
            uploadClicked
          }
        >
          Upload
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={() => setOpen(false)}
          ref={cancelButtonRef}
        >
          Cancel
        </button>
      </div>
    </TransitionDialog>
  );
}
